import { Children, ComponentType, ReactNode, useState } from 'react';

import { useDraggable } from '@dnd-kit/core';
import styled from 'styled-components';

import { Slide } from '@components/animators/slide';
import { FieldDefaultProps, FieldId, FieldTypeKind } from '@components/form/v2';
import { IconProps } from '@components/icons';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { randomId } from '@utils/strings';
import { transitionStandard } from '@utils/transitions';

import { useFormBuilderContext } from './context';
import { SmallDragHandle } from './drag-handle';
import { ElementAccordion } from './element-accordion';
import { getFieldGroups } from './field-registry';

export const ElementsList = ({ extraFieldGroups }: { extraFieldGroups?: ReactNode }) => {
  const {
    store: { useFormType },
  } = useFormBuilderContext();

  const { t } = useScopedTranslate(locale);
  const { content, inputs, selections, structure, special } = getFieldGroups();
  const formType = useFormType();

  const structureFields =
    formType === 'form' ? structure : structure.filter((field) => field.kind === 'separator');

  return (
    <Slide key="add" offset={-30}>
      <Stack space={2}>
        {extraFieldGroups}

        <FieldGroup label={t('content')} hideEmpty>
          {content.map((field) => {
            return (
              <FieldGroup.AddElement
                kind={field.kind}
                key={field.kind}
                Icon={field.Icon}
                label={field.sidebarTitle ?? field.title}
                color="greenTrans10"
                labelColor="elementGreen200"
              />
            );
          })}
        </FieldGroup>

        <FieldGroup label={t('structure')} hideEmpty>
          {structureFields.map((field) => {
            return (
              <FieldGroup.AddElement
                kind={field.kind}
                key={field.kind}
                Icon={field.Icon}
                label={field.sidebarTitle ?? field.title}
                color="indigoTrans10"
                labelColor="elementIndigo100"
              />
            );
          })}
        </FieldGroup>

        {formType === 'form' && (
          <FieldGroup label={t('inputs')} hideEmpty>
            {inputs.map((field) => {
              return (
                <FieldGroup.AddElement
                  kind={field.kind}
                  key={field.kind}
                  Icon={field.Icon}
                  label={field.sidebarTitle ?? field.title}
                  color="orangeTrans10"
                  labelColor="elementOrange200"
                />
              );
            })}
          </FieldGroup>
        )}

        {formType === 'form' && (
          <FieldGroup label={t('selections')} hideEmpty>
            {selections.map((field) => {
              return (
                <FieldGroup.AddElement
                  kind={field.kind}
                  key={field.kind}
                  Icon={(props) => <field.Icon {...props} color="elementBlue100" />}
                  label={field.sidebarTitle ?? field.title}
                  color="blueTrans10"
                  labelColor="elementPurpleInvert"
                />
              );
            })}
          </FieldGroup>
        )}
        {formType === 'form' && (
          <FieldGroup label={t('special')} hideEmpty>
            {special.map((field) => {
              return (
                <FieldGroup.AddElement
                  kind={field.kind}
                  key={field.kind}
                  Icon={(props) => <field.Icon {...props} color="elementOrange200" />}
                  label={field.sidebarTitle ?? field.title}
                  color="orangeTrans10"
                  labelColor="elementPurpleInvert"
                />
              );
            })}
          </FieldGroup>
        )}
      </Stack>
    </Slide>
  );
};

const Element = ({
  Icon,
  iconColor,
  label,
  color,
  onClick,
}: {
  Icon: ComponentType<IconProps>;
  label: string;
  iconColor?: Color;
  color?: Color;
  onClick(): void;
}) => {
  return (
    <ElementContainer onClick={onClick}>
      <IconContainer $color={color}>
        <Icon size={16} color={iconColor ?? 'elementPurple100'} />
      </IconContainer>
      <Text font="regular14" text={label} />
    </ElementContainer>
  );
};

const AddElement = <Kind extends FieldTypeKind>({
  Icon,
  label,
  color,
  labelColor,
  kind,
  createProps,
}: {
  createProps?: Partial<FieldDefaultProps<Kind> & { id?: FieldId }>;
  Icon: ComponentType<IconProps>;
  label: string;
  color?: Color;
  labelColor?: Color;
  kind: FieldTypeKind;
}) => {
  const {
    store: { actions },
  } = useFormBuilderContext();
  const dragId = useState(() => randomId())[0];
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `insert-${dragId}`,
    data: {
      isInsertingField: true,
      createProps,
      kind,
    },
  });

  return (
    <ElementContainer
      ref={setNodeRef}
      onClick={() => {
        actions.addField(kind, createProps);
      }}
      {...attributes}
      {...listeners}
    >
      <IconContainer $color={color}>
        <Icon size={16} color={labelColor ?? 'elementPurple100'} />
      </IconContainer>
      <Text font="regular14" text={label} ellipsisWithTooltip />
      <SmallDragHandle data-drag-handle="true" />
    </ElementContainer>
  );
};

const FieldGroup = ({
  label,
  children,
  hideEmpty = false,
}: {
  label: string;
  children: ReactNode;
  hideEmpty?: boolean;
}) => {
  const allowedChildren = Children.toArray(children).filter(Boolean);
  if (allowedChildren.length === 0 && hideEmpty) {
    return null;
  }
  return (
    <ElementAccordion font="semibold12" fontColor="textSecondary" label={label}>
      {children}
    </ElementAccordion>
  );
};

FieldGroup.Element = Element;
FieldGroup.AddElement = AddElement;
ElementsList.FieldGroup = FieldGroup;

const ElementContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(8),
  width: '100%',
  background: 'transparent',
  margin: 0,
  transition: transitionStandard('background'),
  height: pxToRem(32),
  borderRadius: pxToRem(4),
  padding: `0 ${pxToRem(4)}`,
  '> span': {
    flexGrow: 1,
  },

  '[data-drag-handle="true"]': {
    display: 'none',
  },

  '&:hover': {
    background: theme.color.uiTrans5,
    cursor: 'pointer',

    '[data-drag-handle="true"]': {
      display: 'block',
    },
  },
});

const IconContainer = styled.div<{ $color?: Color }>((p) => ({
  borderRadius: pxToRem(4),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: p.$color ? theme.color[p.$color] : 'transparent',
  height: pxToRem(24),
  width: pxToRem(24),
  flexShrink: 0,
}));

const locale = {
  en: {
    content: 'Content',
    structure: 'Structure',
    inputs: 'Inputs',
    selections: 'Selections',
    special: 'Special',
  },
};
